// LEGAL TEMPLATE NOTICE: This is a business / legal template and
// must be reviewed by qualified counsel before production. Do not
// promise instant refunds; verify the timeline and language with
// your payment partner and counsel. Update placeholders before
// going live.

import { Link } from 'react-router-dom';
import {
  LegalSection,
  LegalList,
  LegalCallout
} from '../../components/legal/LegalSection';

export default function RefundPolicyPage() {
  return (
    <article>
      <header className="mb-2">
        <h1 className="text-3xl sm:text-4xl font-bold text-ink-900 m-0 leading-tight">
          Refund &amp; Cancellation Policy
        </h1>
        <p className="mt-2 text-sm text-ink-500 m-0">
          Last updated: May 2026
        </p>
      </header>

      <p className="mt-5 text-sm sm:text-[15px] text-ink-700 leading-relaxed">
        This policy applies to payments made to Glass-i for SaaS plans
        through Razorpay and our banking / payment partners. Please also
        review our{' '}
        <Link to="/terms" className="text-brand-600 hover:text-brand-700">
          Terms of Service
        </Link>
        .
      </p>

      <LegalSection title="1. Overview">
        <p>
          Glass-i provides subscription-based SaaS access. Refunds and
          cancellations are handled in line with the rules described below
          and applicable law. Accepted refunds will be processed within a
          reasonable period after verification.
        </p>
      </LegalSection>

      <LegalSection title="2. Trial Plan (₹99 including GST)">
        <LegalList
          items={[
            'The Trial Plan grants limited evaluation access for the period stated in-app.',
            'Trial Plan payments are generally non-refundable after the account has been activated or approved.',
            'Refunds may be considered in case of a duplicate payment, a failed provisioning where access was never granted, account rejection by Glass-i during approval, or a verifiable payment-error scenario.'
          ]}
        />
      </LegalSection>

      <LegalSection title="3. Standard Plan">
        <LegalList
          items={[
            'Standard Plan subscription payments are generally non-refundable after the account has been activated for the billing period in question.',
            'If your account is rejected during the approval process after payment, a refund will be initiated as per this policy.',
            'Plan upgrades and downgrades, where supported, will be handled as described in-app at the time of change.'
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Custom Plans">
        <LegalList
          items={[
            'Custom Plan payments are based on the agreed custom code and the terms displayed during checkout.',
            'Refunds for Custom Plans are subject to the agreed custom terms, failed provisioning, duplicate payment, or account rejection by Glass-i.'
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Duplicate or failed payments">
        <p>
          If an amount is debited from your account but the payment
          verification fails, please contact our support team with the
          payment reference (Razorpay payment / order ID) so we can verify
          the transaction with the payment gateway. If a duplicate payment
          is confirmed, the duplicate amount will be initiated for refund.
        </p>
      </LegalSection>

      <LegalSection title="6. Cancellation">
        <LegalList
          items={[
            'You may stop renewals or cancel your subscription where supported in-app.',
            'Cancellation does not automatically refund a billing period that has already been paid for and is in use, unless required by law or stated otherwise in the plan terms.',
            'Any cancellation charges, if applicable, will be disclosed before purchase. We do not impose hidden cancellation charges.'
          ]}
        />
      </LegalSection>

      <LegalSection title="7. Refund timeline">
        <LegalCallout tone="info">
          Accepted refunds will normally be initiated within 7 working days
          after verification. The amount will then be credited as per your
          bank&apos;s and / or the payment gateway&apos;s timelines, which
          may vary.
        </LegalCallout>
      </LegalSection>

      <LegalSection title="8. Refund mode">
        <p>
          Where possible, refunds are returned to the original payment
          method through Razorpay or the relevant payment partner. If the
          original method cannot accept a refund, an alternative method may
          be used, which we will confirm with you.
        </p>
      </LegalSection>

      <LegalSection title="9. Non-refundable cases">
        <p>
          The following are generally non-refundable, subject to applicable
          law:
        </p>
        <LegalList
          items={[
            'Completed billing periods that have already been used.',
            'Suspension or termination caused by violation, misuse, or breach of our Terms by the customer.',
            'Issues caused by incorrect information provided by the customer at signup.',
            'Services already consumed during the billing period.'
          ]}
        />
      </LegalSection>

      <LegalSection title="10. Contact">
        <p>
          For refund requests or questions about this policy, please contact
          us with the following details to help us resolve your request
          quickly: registered email, registered mobile, GSTIN, Razorpay
          payment / order ID (if available), and the date, time, and amount
          of the transaction.
        </p>
        <div className="rounded-xl border border-surface-200 bg-surface-50 px-4 py-3 text-sm text-ink-700 space-y-1">
          <p className="m-0">
            Email:{' '}
            <a
              href="mailto:support@glass-i.com"
              className="text-brand-600 hover:text-brand-700"
            >
              support@glass-i.com
            </a>
          </p>
          <p className="m-0">Phone: +91 98765 43210</p>
          <p className="m-0">
            Hours: Monday to Saturday, 10:00 AM to 6:00 PM IST, excluding
            public holidays
          </p>
        </div>
      </LegalSection>
    </article>
  );
}
